import React, {useState, useEffect} from 'react';
import { PackageTrackingDetails, RequestChangeResponse, RequestChangePostResponse, ServiceLocation, OpeningHour } from '../../../types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SelectorComponent from '../Shared/DropdownSelector/SelectorComponent';
import ActionIcon from '../Shared/Icons/ActionIcon';
import { InputRow, InputLabel, SelectorWrapper, TextInputWrapper, SubmitButton, OptionContainer, SelectedOptionContainer, SpinnerContainer } from './RequestChangeInputs';
import toast from '../../../Scripts/App/displayToastMessage';
import moment from 'moment';
import FloatInputContainer from '../Shared/TextInput/FloatInputContainer';
import { TFunction } from 'i18next';

const ServiceLocationContainer = styled(OptionContainer)`
    
`;

const ServiceLocationDetailsWrapper = styled.div.attrs(p => ({
    style: {
        opacity: p.visible ? '1' : '0',
        left: `${(p.pos?.x ?? 0)}px`,
        top: `${(p.pos?.y ?? 0)}px`,
    }
}))`
    position: fixed;
    z-index: 100001;
    background-color: #F6F7F7;
    border: 1px solid #000;
    padding: 5px 10px;
    margin: 10px;
    transition: opacity 0.3s ease-in 0.5s;
`;

const ServiceLocationDetails = ({serviceLocation}: {serviceLocation?: ServiceLocation}) => {
    const [t] = useTranslation();
    const [pos, setPos] = useState<{x: number, y: number}>(); //mouse position

    // Add mousemove listener to get mouse position
    useEffect(() => {
        const setFromEvent = e => setPos({ x: e.clientX, y: e.clientY });
        window.addEventListener("mousemove", setFromEvent);
        
        return () => {
          window.removeEventListener("mousemove", setFromEvent);
        };
    }, []);

    return(
        <ServiceLocationDetailsWrapper 
            pos={pos}
            visible={serviceLocation && pos?.x && pos?.y}
        >
            <div><strong>{serviceLocation?.Name}</strong></div>
            <div>{serviceLocation?.AddressLine1}</div>
            <div>{serviceLocation?.AddressLine2}</div>
            <div>{serviceLocation?.AddressLine3}</div>
            <div>{serviceLocation?.City}</div>
            {serviceLocation?.OpeningHours ?
                <>
                    <hr />
                    <div>{t('service_location_open_hours')}:</div>
                    {serviceLocation?.OpeningHours.map(h =>
                        <div>{h.Day}: {h.Opening} - {h.Closing}</div>
                    )}
                </>
            : serviceLocation?.OpeningHoursUnformatted ?
                <>
                    <hr />
                    <div>{t('service_location_open_hours')}:</div>
                    {serviceLocation?.OpeningHoursUnformatted.map(h =>
                        <div>{h}</div>
                    )}
                </>
            : 
                null
            }
        </ServiceLocationDetailsWrapper>
    );
};

interface ServiceLocationData extends RequestChangeResponse {
    ServiceLocations: ServiceLocation[];
};

interface DeliverToServiceLocationProps {
    packageDetails: PackageTrackingDetails;
};

const DeliverToServiceLocationComponent = ({packageDetails}: DeliverToServiceLocationProps) => {
    const [t] = useTranslation();
    const [serviceLocationData, setServiceLocationData] = useState({IsLoaded: false, Data: {} as ServiceLocationData});
    const [serviceLocationDetails, setServiceLocationDetails] = useState<string>();
    const [selectedServiceLocation, setSelectedServiceLocation] = useState<ServiceLocation>();
    const [hoveringServiceLocation, setHoveringServiceLocation] = useState<ServiceLocation>();
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string[]} | undefined>();
    const useCustomServiceLocation = (): boolean => serviceLocationData?.Data?.ServiceLocations?.length > 0;

    const loadServiceLocationData = () => {
        const xhr = new XMLHttpRequest();
        const url = `Package/LeaveAtServiceLocationData` + 
                    `?jobId=${packageDetails.JobId}` + 
                    `&trackingNumber=${packageDetails.TrackingNumber}` +
                    `&isDelivery=${packageDetails.DeliveryOptions.IsDelivery}`;
        xhr.open('GET', url, true);
        xhr.onload = () => {
            const data = JSON.parse(xhr.responseText) as ServiceLocationData;
            setServiceLocationData({IsLoaded: true, Data: data});

            if(data.Success && data.ServiceLocations && data.ServiceLocations.length > 0)
            {
                setSelectedServiceLocation(data.ServiceLocations[0]);
            }
            if(data.Message)
            {
                toast(data.Message, data.MessageType ?? 'success');
            }
        };
        xhr.send();
    };

    const reloadServiceLocationData = () => {
        setServiceLocationData({IsLoaded: false, Data: {} as ServiceLocationData});
        loadServiceLocationData();
    };

    const sendServiceLocationRequest = () => {
        let details = serviceLocationDetails;
        if (selectedServiceLocation) {
            details = [
                selectedServiceLocation.Name,
                selectedServiceLocation.AddressLine1,
                selectedServiceLocation.AddressLine2,
                selectedServiceLocation.AddressLine3,
                selectedServiceLocation.City
            ].join(', ');
        }

        if (!details) {
            return;
        }

        const xhr = new XMLHttpRequest();
        const url = 'Package/LeaveAtServiceLocation'
        xhr.open('POST', url);
        xhr.setRequestHeader('Content-type', 'application/json');
        const data = JSON.stringify({
            TrackingNumber: packageDetails.TrackingNumber,
            JobId: packageDetails.JobId,
            IsDelivery: packageDetails.DeliveryOptions.IsDelivery,
            Details: details
        });
        xhr.onload = () => {
            const response = JSON.parse(xhr.responseText) as RequestChangePostResponse;
            if(response.Success)
            {
                // refresh the page
                const successMessage = (packageDetails.DeliveryOptions.IsDelivery 
                    ? t('job_updated_delivery_service_location') 
                    : t('job_updated_pickup_service_location')
                ).replace('{0}', t('service_location'));
                window.location.href = `Package/Details?trackingNumber=${packageDetails.TrackingNumber}&message=${successMessage}`;
            }
            else
            {
                setValidationErrors(response.ValidationErrors);
                if(response.Message)
                {
                    toast(response.Message, response.MessageType ?? 'success');
                }
            }
        }
        xhr.send(data);
    };

    const renderSelectedServiceLocation = (serviceLocation: ServiceLocation) => {
        return(
            <SelectedOptionContainer
                onMouseOver={() => setHoveringServiceLocation(serviceLocation)}
                onMouseLeave={() => setHoveringServiceLocation(undefined)}
            >
                {serviceLocation.Name}
            </SelectedOptionContainer>
        );
    };

    const renderServiceLocation = (serviceLocation: ServiceLocation) => {
        return(
            <>
                <ServiceLocationContainer 
                    key={serviceLocation.Id} 
                    onClick={() => {
                        setSelectedServiceLocation(serviceLocation);
                        setHoveringServiceLocation(undefined);
                    }}
                    onMouseOver={() => setHoveringServiceLocation(serviceLocation)}
                    onMouseLeave={() => setHoveringServiceLocation(undefined)}
                >
                    {serviceLocation.Name}
                </ServiceLocationContainer>
                {}
            </>
        );
    };

    useEffect(() => {
        loadServiceLocationData();
    }, []);

    if(!serviceLocationData.IsLoaded) //Loading
        return <SpinnerContainer />;
    
    else if(!serviceLocationData.Data.Success) //Load error
        return(
            <InputRow>
                <div>{t('request_change_load_error_message')}</div>
                <ActionIcon title={'refresh'} onClick={reloadServiceLocationData}>refresh</ActionIcon>
            </InputRow>
        );

    const hasServiceLocationDetails = Boolean(serviceLocationDetails || selectedServiceLocation);

    return(
        <>
            {hoveringServiceLocation && <ServiceLocationDetails serviceLocation={hoveringServiceLocation} />}

            {useCustomServiceLocation() ? 
                <InputRow>
                    <InputLabel>{t('service_location_available')}</InputLabel>
                    <SelectorWrapper>
                        <SelectorComponent 
                            label={t('request_change_no_options_available')}
                            selectedItem={selectedServiceLocation}
                            items={serviceLocationData.Data.ServiceLocations}
                            renderSelectedItem={renderSelectedServiceLocation}
                            renderItems={(serviceLocations) => serviceLocations.map(renderServiceLocation)}
                            fitToSize={false}
                        />
                    </SelectorWrapper>
                </InputRow> : 
                <InputRow>
                    <InputLabel>{t('service_location_details')}</InputLabel>
                    <TextInputWrapper>
                        <FloatInputContainer 
                            value={serviceLocationDetails ?? ''} 
                            changeAction={(e) => setServiceLocationDetails(e.currentTarget.value)}
                            showErrors={true}
                            isValid={!(validationErrors && validationErrors!["SafePlaceDetails"]?.length > 0)}
                            invalidMessages={validationErrors && validationErrors!["SafePlaceDetails"]}
                        />
                    </TextInputWrapper>
                </InputRow>
            }
            <SubmitButton handleClick={sendServiceLocationRequest} disabled={!hasServiceLocationDetails} />
        </>
    );
};

export default DeliverToServiceLocationComponent;